import { RelocationItemInput } from "@/gql/graphql";
import { ImportColumnId } from "@/app/Import/Utils/defaultColumnOrder";
import dayjs from "dayjs";

export type TransformOptions = {
  fullOrder: ImportColumnId[];
  hiddenColIds: ImportColumnId[];
  ignoreColumns: string;
  ignoreHeaderRow: boolean;
  ignoreMissingLine: boolean;
};

export function transformFileInputToRowData(
  rawData: any[][],
  {
    fullOrder,
    hiddenColIds,
    ignoreColumns,
    ignoreHeaderRow,
    ignoreMissingLine,
  }: TransformOptions,
): RelocationItemInput[] {
  const ignoreColumnIndexes = ignoreColumns
    .split(",")
    .map((col) => col.trim())
    .filter(Boolean) // prevent "" -> -1
    .map((col) => lettersToNumber(col) - 1);

  const hasHeaderRow = getHasHeaderRow(rawData);

  // INDEX-BASED mapping only:
  // Build a mapping from spreadsheet column index to field ID
  // IMPORTANT: Keep nulls to maintain index alignment with spreadsheet columns

  // First, filter out hidden fields from fullOrder since they don't consume spreadsheet columns
  const visibleFields = fullOrder.filter(
    (fieldId) => !hiddenColIds.includes(fieldId),
  );

  return rawData
    .filter((row, idx) => {
      if (
        row.find((r) => r !== null && r !== undefined && r !== "") === undefined
      ) {
        return false;
      }

      //If we've settings tell us the first row is a header, drop it
      if (ignoreHeaderRow && idx === 0) {
        return false;
      }

      //If the first row has been detected to be a header, drop it
      if (hasHeaderRow && idx === 0) {
        return false;
      }

      return true;
    })
    .map((row) => {
      const entries = row
        .filter((_, colIdx) => {
          //Filter out the ignored columns
          return !ignoreColumnIndexes.includes(colIdx);
        })
        .map((cell, colIdx) => {
          const colId = visibleFields[colIdx];

          //If we've over indexed, drop the field
          if (!colId) {
            return null;
          }

          if (cell === null || cell === undefined || cell === "") {
            return [colId, null];
          }

          if (colId === "available_from" || colId === "available_to") {
            return [colId, dayjs(cell).format("YYYY-MM-DD")];
          }

          if (
            colId === "fuel_amount" ||
            colId === "hire_units_allowed" ||
            colId === "minimum_hire_units" ||
            colId === "extra_hire_units_allowed"
          ) {
            const stringValue = String(cell);
            const isNegative = stringValue.includes("-");
            const digitsAndDecimal = stringValue.replace(/[^\d.]/g, "");
            const parts = digitsAndDecimal.split(".");
            const cleaned =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : digitsAndDecimal;
            const parsed = parseFloat(
              isNegative && !cleaned.startsWith("-") ? `-${cleaned}` : cleaned,
            );

            return [colId, isNaN(parsed) ? null : parsed];
          }

          if (
            (colId === "line" ||
              colId === "departure_office_code" ||
              colId === "delivery_office_code" ||
              colId === "vehicle_code") &&
            typeof cell === "number"
          ) {
            return [colId, cell.toString()];
          }

          if (
            colId === "fuel_note" &&
            (cell === "0" || cell === "$0" || cell === 0)
          ) {
            return [colId, ""];
          }

          return [colId, cell];
        })
        .filter(Boolean);

      return Object.fromEntries(entries) as RelocationItemInput;
    })
    .filter((row) => {
      if (!ignoreMissingLine) return true;
      return Boolean(row.line);
    });
}

function getHasHeaderRow(rawData: any[][]) {
  const requiredHeaders = [
    "vehicle code",
    "drop off location code",
    "pickup location code",
    "available from date",
    "available to date",
  ];

  const firstRow = rawData[0];

  return firstRow
    ? requiredHeaders.every((requiredHeader) =>
        firstRow.some(
          (cell) =>
            cell &&
            typeof cell === "string" &&
            cell.trim().toLowerCase() === requiredHeader.toLowerCase(),
        ),
      )
    : false;
}

// convert A to 1, Z to 26, AA to 27
function lettersToNumber(letters: string): number {
  return letters.split("").reduce((r, a) => r * 26 + parseInt(a, 36) - 9, 0);
}
